import React, { Suspense, lazy } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import 'font-awesome/css/font-awesome.min.css';
import '../components/styles.css';
import { history } from '../helpers';
import { isTokenExpired } from '../helpers/authService';
import { handleSessionEnd } from '../helpers/common';
import PrivateRoute from '../components/PrivateRoute';
import Header from '../components/pages/Header';
import PageLoader from '../components/common/PageLoader';
import Toast from '../services/toast';
import 'react-toastify/dist/ReactToastify.css';
import { ROLES } from '../constants';

import { LoginPage } from '../components/pages/LoginPage';
import ForgotPassword from '../components/pages/LoginPage/ForgotPassword';
import ValidateOtp from '../components/pages/LoginPage/ValidateOtp';
import ResetPassword from '../components/pages/LoginPage/ResetPassword';

function RouteFallback() {
    return <PageLoader message="Loading..." fullscreen />;
}

const GameMaintenance = lazy(() => import('../components/pages/GameMaintenance/GameMaintenance'));
const AddQuestion = lazy(() => import('../components/pages/GameMaintenance/AddQuestion'));
const EditQuestion = lazy(() => import('../components/pages/GameMaintenance/EditQuestion'));
const QuestionsList = lazy(() => import('../components/pages/GameMaintenance/QuestionsList'));
const BlockedWords = lazy(() => import('../components/pages/ChatPages/BlockedWords'));
const ClassDetails = lazy(() => import('../components/pages/ClassSetup/ClassDetails'));
const AddClassDetails = lazy(() => import('../components/pages/ClassSetup/AddClassDetails'));
const EditClassDetails = lazy(() => import('../components/pages/ClassSetup/EditClassDetails'));
const PopupMessages = lazy(() => import('../components/pages/PopupMessages/PopupMessages'));
const AdMessages = lazy(() => import('../components/pages/PopupMessages/AdMessages'));
const FavoriteActivties = lazy(() => import('../components/pages/ProfileSettings/FavoriteActivties'));
const MediaUpload = lazy(() => import('../components/pages/ProfileSettings/MediaUpload'));
const ChatMonitoring = lazy(() => import('../components/pages/ChatPages/ChatMonitoring'));
const ChatHistory = lazy(() => import('../components/pages/ChatPages/ChatHistory'));
const AdminSetup = lazy(() => import('../components/pages/ProfileSettings/AdminSetup'));
const FontUpload = lazy(() => import('../components/pages/FontSettings/FontUpload'));
const FontSize = lazy(() => import('../components/pages/FontSettings/FontSize'));
const OrganizationBranding = lazy(() => import('../components/pages/ProfileSettings/OrganizationBranding'));

const TOKEN_EXPIRY_CHECK_INTERVAL_MS = 60000; // 1 minute
const RESUME_SESSION_CHECK_DEBOUNCE_MS = 500;

class App extends React.Component {
    tokenExpiryIntervalId = null;
    visibilityListener = null;
    visibilityDebounceTimer = null;

    componentDidMount() {
        if (isTokenExpired()) {
            handleSessionEnd('expired');
            return;
        }
        this.startTokenExpiryCheck();
        this.visibilityListener = () => {
            if (document.visibilityState === 'hidden') {
                if (this.visibilityDebounceTimer) {
                    clearTimeout(this.visibilityDebounceTimer);
                    this.visibilityDebounceTimer = null;
                }
                return;
            }
            if (document.visibilityState !== 'visible') return;
            this.visibilityDebounceTimer = setTimeout(() => {
                this.visibilityDebounceTimer = null;
                if (isTokenExpired()) {
                    handleSessionEnd('expired');
                }
            }, RESUME_SESSION_CHECK_DEBOUNCE_MS);
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
    }

    componentWillUnmount() {
        this.stopTokenExpiryCheck();
        if (this.visibilityDebounceTimer) {
            clearTimeout(this.visibilityDebounceTimer);
            this.visibilityDebounceTimer = null;
        }
        if (this.visibilityListener) {
            document.removeEventListener('visibilitychange', this.visibilityListener);
            this.visibilityListener = null;
        }
    }

    startTokenExpiryCheck() {
        this.stopTokenExpiryCheck();
        this.tokenExpiryIntervalId = setInterval(() => {
            if (isTokenExpired()) {
                this.stopTokenExpiryCheck();
                handleSessionEnd('expired');
            }
        }, TOKEN_EXPIRY_CHECK_INTERVAL_MS);
    }

    stopTokenExpiryCheck() {
        if (this.tokenExpiryIntervalId) {
            clearInterval(this.tokenExpiryIntervalId);
            this.tokenExpiryIntervalId = null;
        }
    }

    render() {
        return (
            <div>
                <Toast />
                <Router history={history}>
                    <Switch>
                        <Route path="/login" component={LoginPage} />
                        <Route path="/forgotpassword" component={ForgotPassword} />
                        <Route path="/validateotp" component={ValidateOtp} />
                        <Route path="/reset-password" component={ResetPassword} />
                        <Route
                            render={() => (
                                <>
                                    <Header />
                                    <Suspense fallback={<RouteFallback />}>
                                        <Switch>
                                            <PrivateRoute exact path="/" component={ChatMonitoring} />
                                            <PrivateRoute exact path="/chathistory" component={ChatHistory} />
                                            <PrivateRoute path="/gamemaintenance" roles={[ROLES.ADMIN]} component={GameMaintenance} />
                                            <PrivateRoute path="/addquestion" roles={[ROLES.ADMIN]} component={AddQuestion} />
                                            <PrivateRoute path="/questionslist/:id" roles={[ROLES.ADMIN]} component={QuestionsList} />
                                            <PrivateRoute path="/editquestion/:id" roles={[ROLES.ADMIN]} component={EditQuestion} />
                                            <PrivateRoute path="/classsetup" component={ClassDetails} />
                                            <PrivateRoute path="/addclassdetails" component={AddClassDetails} />
                                            <PrivateRoute path="/editclassdetails/:id" component={EditClassDetails} />
                                            <PrivateRoute path="/popupmessages" roles={[ROLES.ADMIN]} component={PopupMessages} />
                                            <PrivateRoute path="/admessages" roles={[ROLES.ADMIN]} component={AdMessages} />
                                            <PrivateRoute path="/favoriteactivties" roles={[ROLES.ADMIN]} component={FavoriteActivties} />
                                            <PrivateRoute path="/mediaupload" roles={[ROLES.ADMIN]} component={MediaUpload} />
                                            <PrivateRoute path="/blockedwords" roles={[ROLES.ADMIN]} component={BlockedWords} />
                                            <PrivateRoute path="/adminsetup" roles={[ROLES.ADMIN]} component={AdminSetup} />
                                            <PrivateRoute path="/organizationbranding" roles={[ROLES.ADMIN]} component={OrganizationBranding} />
                                            <PrivateRoute path="/fontupload" roles={[ROLES.ADMIN]} component={FontUpload} />
                                            <PrivateRoute path="/fontsize" roles={[ROLES.ADMIN]} component={FontSize} />
                                            <Redirect from="*" to="/" />
                                        </Switch>
                                    </Suspense>
                                </>
                            )}
                        />
                    </Switch>
                </Router>
            </div>
        );
    }
}

export default App;
